// The single canonical Orb. Always rendered with layoutId="orb" so Framer
// Motion handles position/size morphs between templates natively.
//
// Doctrine §0.7: one element, one DOM node. Templates control its placement
// via grid/absolute positioning of the Orb's wrapper, never by remounting.
//
// Phase 2.5a: when the agent is speaking, the orb scales up subtly and the
// glow brightens. The CSS `animate-orb-breathe` keyframes still drive the
// idle baseline; the speaking state stacks on top via inline styles so the
// breathe animation isn't overridden.
//
// Phase 2.5e: a "nudge pulse" rides on the same boosted-state path. After
// audio_done + 12s, useNudgePulse flips true for ~600ms — the orb does ONE
// soft pulse cycle as a "what next?" cue. We also tag the DOM with
// `data-orb-active` ('idle' | 'speaking' | 'nudge') so external observers
// (Playwright smoke, CSS hooks) can read state without inspecting style.

import { motion } from 'framer-motion';
import { orbSpring } from '../motion/variants';
import { useSpeakingState } from './useSpeakingState';
import { useIdleDrift } from './useIdleDrift';
import { useNudgePulse } from './useNudgePulse';

interface OrbProps {
  /** Diameter in pixels. Templates set their preferred size. */
  size?: number;
  className?: string;
}

export function Orb({ size = 88, className = '' }: OrbProps) {
  const speaking = useSpeakingState();
  const nudge    = useNudgePulse();
  // Same boosted scale/glow drives both speaking and nudge.
  const boosted  = speaking || nudge;
  // Idle drift only kicks in when nothing more interesting is happening.
  const drift = useIdleDrift(!boosted);
  const stateAttr = speaking ? 'speaking' : nudge ? 'nudge' : 'idle';
  return (
    <motion.div
      layoutId="orb"
      // Layout transitions (template-to-template morphs) keep the spring;
      // speaking-state and drift transitions use a smooth tween. Per-property
      // transitions are merged by Framer Motion.
      transition={{
        layout:    orbSpring,
        scale:     { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        filter:    { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        boxShadow: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
        x:         { duration: 2.4,  ease: [0.45, 0, 0.55, 1] },
        y:         { duration: 2.4,  ease: [0.45, 0, 0.55, 1] },
      }}
      // Drop the breathe animation while boosted — the boosted transform
      // is more interesting and the two would fight. Resume on idle.
      className={`rounded-full bg-accent-glow shadow-orb ${boosted ? '' : 'animate-orb-breathe'} ${className}`}
      animate={{
        x:      drift.x,
        y:      drift.y,
        scale:  boosted ? 1.08 : 1.0,
        filter: boosted ? 'brightness(1.18) saturate(1.1)' : 'brightness(1.0) saturate(1.0)',
        boxShadow: boosted
          ? '0 0 100px 6px rgba(168,181,255,0.55), 0 0 240px 0 rgba(124,140,255,0.30)'
          : '0 0 80px 0 rgba(124,140,255,0.45), 0 0 200px 0 rgba(124,140,255,0.18)',
      }}
      style={{ width: size, height: size }}
      data-orb-active={stateAttr}
      aria-hidden="true"
    />
  );
}
